import React, { useEffect, useMemo, useState } from "react"
import Box from "@mui/material/Box"
import Tabs from "@mui/material/Tabs"
import Tab from "@mui/material/Tab"
import { useLocation, useParams } from "react-router-dom"
import { BankAccountIcon, BankCardIcon } from "../../components/Icons/Icons"
import PaymentBankViewTab from "../../components/PaymentMethodTab/PaymentBankViewTab"
import PaymentAddressViewTab from "../../components/PaymentMethodTab/PaymentAddressViewTab"
import PaymentCardViewTab from "../../components/PaymentMethodTab/PaymentCardViewTab"
import Button from "../../components/Button/Button"
import { useDispatch } from "react-redux"
import { useMyContextLoading } from "../../components/LoadingContextProvider/LoadingContextProvider"
import { editBankAccountLinkById, getBankAccountLinkById } from "../../features/bankAccountLink/bankAccountLink"
import { getLinkToken, readLinkToken } from "../../utils/linkToken"
import {
  validateFormBankAccount,
  validateFormCreditCard
} from "../Contacts/add-contact/CreditSelect/helper"
import CardVerificationPanel from "../../components/CardVerification/CardVerificationPanel"
import { verificationSessionReceived, cardVerificationReset } from "../../features/cardVerification/cardVerification"
import ExpiredLink, { EXPIRED_LINK_MESSAGE } from "./ExpiredLink"
import { ACH, acceptsAch, acceptsCard, defaultMethodOf } from "../../constants/paymentMethods"
import { useUiKit } from "../../components/UiKit/UiKitContext"

const UNAVAILABLE_MESSAGE = 'Online payment details cannot be added here for your account. Your office will contact you about payment.'

const ACH_TAB = 0
const CARD_TAB = 1

const CreateBankAccount = ({ onNextStep, setIsActive }) => {
  const dispatch = useDispatch()
  const { userId } = useParams()
  const { search } = useLocation()

  // WS 1406230 / WS 1408855 — the scoped onboarding-link token. It now arrives in the URL
  // fragment and is lifted out at module scope, so `search` no longer holds it by the time this
  // runs; readLinkToken stays as the fallback for a link opened before that capture ran. Absent
  // means an invitation sent before either fix, which the backend still honours on the legacy
  // `:userId` routes (the grace path); we must not change one byte of what that flow sends.
  const linkToken = useMemo(() => getLinkToken() || readLinkToken(search), [search])

  const [pickedTab, setPickedTab] = useState(null)
  const [triggerUpd, setTriggerUpd] = useState(false)
  const { button: kitButton } = useUiKit()
  const [saving, setSaving] = useState(false)
  const [kitSave, setKitSave] = useState({ tab: null, nonce: 0 })
  const savingProps = kitButton ? { onSaving: (active) => setSaving(active) } : null
  const tabLock = saving ? { disabled: true } : null
  const [address, setAddress] = useState({})
  const [gatewayProvider, setGatewayProvider] = useState("")
  const [acceptedPaymentMethods, setAcceptedPaymentMethods] = useState(undefined)

  const { triggerLoading } = useMyContextLoading()
  const [errors, setErrors] = useState([])
  const [fields, setFields] = useState('')
  const [expiredLinkMessage, setExpiredLinkMessage] = useState(null)
  // Set only after a card is saved on the Credit Card tab. Its presence is what makes the
  // verification step appear, and its absence is what keeps the ACH tab untouched.
  const [verificationSession, setVerificationSession] = useState(null)
  const [cardVerified, setCardVerified] = useState(false)

  const getUserBankAccountLinkById = async () => {
    triggerLoading(true)
    try {
      const resultAction = await dispatch(getBankAccountLinkById({ userId, token: linkToken }))

      // WS 1406230 — a 403 on a TOKENED read means the link itself is no longer usable: expired,
      // rotated by a resend, or scoped to a different contact. Say that plainly instead of
      // rendering an empty form the client can fill in and never manage to submit.
      //
      // Guarded on `linkToken` on purpose. An untokened request has no token to be refused, so a
      // 403 there means something else entirely — the grace path keeps its existing behaviour.
      if (linkToken
        && getBankAccountLinkById.rejected.match(resultAction)
        && resultAction.payload?.status === 403) {
        setExpiredLinkMessage(resultAction.payload?.message || EXPIRED_LINK_MESSAGE)
        return
      }

      setGatewayProvider(resultAction?.payload?.data?.user?.company?.gatewayProvider)
      setAcceptedPaymentMethods(resultAction?.payload?.data?.acceptedPaymentMethods)

      // Optional: BankAccount.jsx renders this component without `setIsActive`, so calling it
      // unconditionally throws there and aborts the rest of this function.
      if (setIsActive) {
        setIsActive(Boolean(resultAction?.payload?.data?.isActual))
      }
    } catch (error) {
      console.error("Error dispatching getBankAccountLinkById:", error)
    } finally {
      triggerLoading(false)
    }
  }

  const updateUserBankAccountLinkByUserId = async () => {
    triggerLoading(true)
    try {
      await dispatch(editBankAccountLinkById({ userId, token: linkToken }))
    } catch (error) {
      console.error("Error dispatching editBankAccountLinkById:", error)
    }
    triggerLoading(false)
  }

  const showAch = acceptsAch(acceptedPaymentMethods)
  const showCard = acceptsCard(acceptedPaymentMethods)
  const defaultTab = defaultMethodOf(acceptedPaymentMethods) === ACH ? ACH_TAB : CARD_TAB
  const pickedTabAccepted = (pickedTab === ACH_TAB && showAch) || (pickedTab === CARD_TAB && showCard)
  const valueTab = pickedTabAccepted ? pickedTab : defaultTab
  const cardOnlyWithoutCards = !showAch && gatewayProvider !== "mint"

  const handleChange = (event, newValue) => {
    setPickedTab(newValue)
  }

  const handleCardSaved = (payload) => {
    setCardVerified(false)
    dispatch(cardVerificationReset())
    const token = payload?.verificationSession?.token || null
    setVerificationSession(token)
    if (token) dispatch(verificationSessionReceived(payload.verificationSession))
  }

  useEffect(() => {
    getUserBankAccountLinkById()
  }, [])

  useEffect(() => {
    valueTab === ACH_TAB ? validateFormBankAccount({ fields, setErrors }) : validateFormCreditCard({ fields, setErrors })
  }, [fields])

  // An explicit dead end, not a toast over a form that cannot be submitted. The client's only way
  // forward is a new invitation, so the page says exactly that and nothing else.
  if (expiredLinkMessage) {
    return <ExpiredLink message={expiredLinkMessage} testId="bank-account-link-expired" />
  }

  if (cardOnlyWithoutCards) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4"
        data-testid="bank-account-link-unavailable">
        <div className="border border-divider bg-paper shadow-lg rounded-2xl p-12 text-center max-w-md">
          <div className="text-text-secondary">{UNAVAILABLE_MESSAGE}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col justify-end items-end border border-divider sm:m-10 m-4 sm:p-10 p-4 rounded-[12px]">
      <div
        className="flex flex-col items-start gap-6 self-stretch pt-6 pb-10 px-6 rounded-2xl border border-divider bg-paper">
        <div className="w-full flex justify-between">
          <div
            className="text-text-secondary font-sans text-xs font-bold leading-[150%] uppercase">
            Payment Method
          </div>
        </div>
        <Box sx={{ maxWidth: { xs: 320, sm: 480 }, bgcolor: "background.paper" }}>
          <Tabs
            value={valueTab}
            onChange={handleChange}
            aria-label="Tabs"
            sx={{
              '.MuiTabs-scroller': {
                height: '35px'
              },
              '.MuiTabs-flexContainer': {
                gap: '40px'
              },
              'button': {
                minHeight: 0,
                padding: 0,
                justifyContent: 'flex-start',
                fontWeight: 600,
                textTransform: 'capitalize'
              },
              'button svg': {
                marginRight: '5px'
              },
              '.Mui-selected': {
                color: 'var(--color-text-primary) !important',
              }
            }}
          >
            {showAch && (
              <Tab iconPosition="start" value={ACH_TAB}
                icon={<BankAccountIcon fill={valueTab === ACH_TAB ? 'var(--color-gray-950)' : 'var(--color-gray-600)'} bgFill={'var(--color-gray-400)'} />}
                label="ACH" {...tabLock} />
            )}
            {showCard && (
              <Tab iconPosition="start" value={CARD_TAB}
                icon={<BankCardIcon fill={valueTab === CARD_TAB ? 'var(--color-gray-950)' : 'var(--color-gray-600)'} bgFill={'var(--color-gray-400)'} />}
                label="Credit Card" {...tabLock} />
            )}
          </Tabs>
        </Box>
        {showAch && (
          <div className={`w-full ${valueTab === ACH_TAB ? '' : 'hidden'}`}>
            <PaymentBankViewTab userId={userId} triggerUpd={kitButton ? (kitSave.tab === ACH_TAB ? kitSave.nonce : 0) : valueTab === ACH_TAB && triggerUpd} address={address} setErrors={setErrors}
              onSuccessFunc={updateUserBankAccountLinkByUserId} onNextStep={onNextStep}
              setFields={setFields} linkToken={linkToken} {...savingProps} />
          </div>
        )}
        {showCard && gatewayProvider === "mint" && (
          <div className={`w-full ${valueTab === CARD_TAB ? '' : 'hidden'}`}>
            {/* No linkToken: POST /cards-link has no token-authenticated variant on the API yet
                (vlg-server!1221 scoped the token to the bank-account endpoints, which are the ones
                carrying the destructive `removeOld`). Passing one here would send the card to a
                route that does not exist. Tokenising the card path is follow-up backend work. */}
            <PaymentCardViewTab userId={userId} address={address} triggerUpd={kitButton ? (kitSave.tab === CARD_TAB ? kitSave.nonce : 0) : valueTab === CARD_TAB && triggerUpd} setErrors={setErrors}
              onSuccessFunc={updateUserBankAccountLinkByUserId} onNextStep={onNextStep}
              setFields={setFields} onCardSaved={handleCardSaved}
              reportRefusals={Array.isArray(acceptedPaymentMethods)} onRefused={() => setTriggerUpd(false)} {...savingProps} />
          </div>
        )}
      </div>
      {valueTab === CARD_TAB && verificationSession && (
        <div className="w-full mt-5">
          <CardVerificationPanel
            sessionToken={verificationSession}
            onVerified={() => setCardVerified(true)}
          />
        </div>
      )}
      <div
        className="flex flex-col items-start gap-6 self-stretch pt-6 pb-10 px-6 rounded-2xl border border-divider bg-paper mt-5">
        <div
          className="payment_method text-text-secondary font-sans text-xs font-bold leading-[150%] uppercase">
          Billing Info
        </div>
        <div className="w-full">
          <PaymentAddressViewTab returnAddress={setAddress} setFields={setFields} setErrors={setErrors} />
        </div>
      </div>
      <Button size="lg" classes="sm:max-w-[200px] w-full sm:m-10 my-4"
        disabled={Object.keys(errors).some((key) => errors[key])
          || (valueTab === CARD_TAB && Boolean(verificationSession) && !cardVerified)}
        loading={saving}
        onClick={() => {
          if (kitButton) {
            if (!saving) setKitSave((prev) => ({ tab: valueTab, nonce: prev.nonce + 1 }))
            return
          }
          setTriggerUpd(!triggerUpd)
          // onNextStep()
        }} width="min-w-[156px]">
        Save
      </Button>

    </div>
  )
}

export default CreateBankAccount
